import { Link } from 'react-router-dom';
import { Card, CardBody } from '@img/ui';

/**
 * 🛡️ صفحة الامتثال — شركة صورة العقارية
 *
 * توضّح التزام المنصة بأنظمة الهيئة العامة للعقار (REGA)
 * ووزارة العدل، ونظام حماية البيانات الشخصية (PDPL).
 */
export function CompliancePage() {
  return (
    <div className="bg-gradient-to-b from-navy-soft/30 to-white">
      {/* 🎯 Hero */}
      <section className="bg-gradient-to-l from-navy-darker via-navy-dark to-navy text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-6xl mb-4">🛡️</div>
          <h1 className="text-3xl md:text-4xl font-black mb-3">
            ملتزمون بأنظمة الهيئة العامة للعقار
          </h1>
          <p className="text-gold-light text-lg mb-6 leading-relaxed">
            منصة شركة صورة العقارية تعمل وفق الاشتراطات الرسمية للهيئة العامة للعقار (REGA)
            وأنظمة المملكة العربية السعودية لحماية حقوق المتعاملين في القطاع العقاري.
          </p>
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 rounded-full px-5 py-2.5 text-sm">
            <span>✓</span>
            <span>كل إعلان عقاري على المنصة معتمَد ومراجَع يدوياً</span>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 py-12">

        {/* 📜 الجهات المُلتَزَم بأنظمتها */}
        <section className="mb-12">
          <h2 className="text-2xl font-black text-navy-dark mb-6 text-center">
            🏛️ الجهات الرسمية التي نلتزم بأنظمتها
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <AuthorityCard
              icon="🏢"
              name="الهيئة العامة للعقار"
              shortName="REGA"
              role="المشرف الرئيسي على القطاع العقاري في السعودية"
              services={[
                'ترخيص الإعلانات العقارية',
                'الاستعلام عن الوسطاء (فال)',
                'البورصة العقارية',
              ]}
              link="https://rega.gov.sa/"
              linkLabel="الموقع الرسمي"
            />
            <AuthorityCard
              icon="⚖️"
              name="وزارة العدل"
              shortName="MOJ"
              role="موثّقة الصكوك ونقل الملكية"
              services={[
                'صكوك الملكية',
                'نقل وإفراغ العقارات',
                'الاستعلام عن الصك',
              ]}
              link="https://srem.moj.gov.sa/deed-inquiry"
              linkLabel="الاستعلام عن صك"
            />
            <AuthorityCard
              icon="🛡️"
              name="نظام حماية البيانات"
              shortName="PDPL"
              role="حماية بيانات المستخدمين الشخصية"
              services={[
                'تشفير البيانات الحساسة',
                'الموافقة الصريحة قبل المشاركة',
                'حق المستخدم في الحذف',
              ]}
              link="https://sdaia.gov.sa/ar/SDAIA/about/Pages/Personal-Data-Protection-Law.aspx"
              linkLabel="نظام PDPL"
            />
          </div>
        </section>

        {/* ✅ خطوات الامتثال */}
        <section className="mb-12">
          <h2 className="text-2xl font-black text-navy-dark mb-2 text-center">
            ✅ كيف نضمن امتثال كل إعلان عقاري؟
          </h2>
          <p className="text-center text-ink-secondary mb-6">
            قبل ظهور أي عقار للزوار، يمر بـ <strong>5 مراحل تحقق</strong> صارمة من فريق المنصة
          </p>

          <div className="space-y-4">
            <Step
              n={1}
              icon="🪪"
              title="التحقق من رخصة فال (وسيط عقاري)"
              text="كل من ينشر عقاراً على المنصة يجب أن تكون لديه رخصة فال سارية ومعتمدة من الهيئة العامة للعقار. نتحقق من الرخصة على البوابة الرسمية قبل قبول الإعلان."
              externalLink="https://eservicesredp.rega.gov.sa/auth/queries/Brokerage"
              externalLinkLabel="بوابة الاستعلام عن الوسطاء"
            />
            <Step
              n={2}
              icon="🏠"
              title="الاطلاع على العقار والبيانات"
              text="فريق المراجعة يفحص الصور (يجب أن تكون حقيقية وتمثّل العقار)، الوصف، الموقع، السعر، والأبعاد. أي تضليل بصري أو معلوماتي يُرفض."
            />
            <Step
              n={3}
              icon="📜"
              title="التحقق من وثائق الملكية"
              text="نراجع رقم صك الملكية على بوابة وزارة العدل (سرم) للتأكد من صحة الملكية وعدم وجود رهون أو نزاعات. لا نقبل عقاراً بدون صك موثّق."
              externalLink="https://srem.moj.gov.sa/deed-inquiry"
              externalLinkLabel="الاستعلام عن صك"
            />
            <Step
              n={4}
              icon="🏢"
              title="التحقق من رخصة الإعلان العقاري"
              text="كل إعلان عقاري في السعودية يلزمه رقم رخصة إعلان مُصدَر من الهيئة العامة للعقار. نتحقق من رقم الرخصة على البوابة الرسمية ونحفظه في سجل الإعلان."
              externalLink="https://eservicesredp.rega.gov.sa/e-services/inquiry-about-the-real-estate-advertisement-license/form"
              externalLinkLabel="الاستعلام عن ترخيص إعلان"
            />
            <Step
              n={5}
              icon="✓"
              title="الاعتماد والنشر"
              text="بعد اكتمال الخطوات، يُعتمد الإعلان رسمياً وتُحفظ بياناته (المراجع، التوقيت، رقم الترخيص) في السجلات الإدارية القانونية. لو فشل أي تحقق، يُرفض الإعلان مع إبلاغ صاحبه بالسبب."
            />
          </div>
        </section>

        {/* 🛡️ ضمانات الجودة والثقة */}
        <section className="mb-12">
          <h2 className="text-2xl font-black text-navy-dark mb-6 text-center">
            🛡️ ضماناتك على المنصة
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Guarantee
              icon="🔒"
              title="بياناتك مشفّرة"
              text="كلمات المرور والمعلومات الحساسة مشفّرة بأحدث تقنيات (bcrypt + SSL). لا نشارك بياناتك مع أي طرف ثالث بدون موافقتك."
            />
            <Guarantee
              icon="📋"
              title="سجل تدقيق كامل"
              text="كل عملية على المنصة (نشر، تعديل، حذف، اعتماد) تُسجَّل مع IP والوقت والمستخدم — لتتبع المساءلة عند الحاجة."
            />
            <Guarantee
              icon="🚫"
              title="لا إعلانات وهمية"
              text="رفضنا للإعلانات غير الموثّقة قاسٍ. لا نقبل أي إعلان بدون صك أو رخصة إعلان عقاري سارية."
            />
            <Guarantee
              icon="⚖️"
              title="حقوقك محفوظة"
              text="في حال أي خلاف بين أطراف التعاقد، توفّر المنصة سجلاً موثّقاً يدعم حقوقك في أي إجراء قانوني."
            />
            <Guarantee
              icon="🎯"
              title="شفافية كاملة"
              text="على كل عقار معتمَد تظهر شارة 'معتمد رسمياً' مع رقم الترخيص وتاريخ المراجعة، ويمكنك التحقق منها مباشرة على بوابة الهيئة."
            />
            <Guarantee
              icon="📞"
              title="دعم متجاوب"
              text="فريق دعم متاح للإبلاغ عن أي إعلان مشبوه أو سلوك غير لائق. كل بلاغ يُراجَع خلال 24 ساعة."
            />
          </div>
        </section>

        {/* 🚩 إذا واجهت مشكلة */}
        <Card className="mb-8 bg-amber-50 border-2 border-amber-200">
          <CardBody className="!py-6">
            <div className="flex items-start gap-4 flex-wrap">
              <div className="text-5xl">🚩</div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-black text-amber-900 mb-2">
                  واجهت إعلاناً مخالفاً أو سلوكاً غير لائق؟
                </h3>
                <p className="text-sm text-amber-800 mb-3 leading-relaxed">
                  نأخذ بلاغات المستخدمين بجديّة كاملة. أي إعلان يخالف الأنظمة سيُسحَب فوراً ويُحال للجهات المختصة عند الحاجة.
                </p>
                <div className="flex gap-2 flex-wrap">
                  <Link
                    to="/contact"
                    className="px-4 py-2 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-sm font-bold"
                  >
                    📨 أبلغ عن مخالفة
                  </Link>
                  <a
                    href="https://rega.gov.sa/"
                    target="_blank"
                    rel="noreferrer"
                    className="px-4 py-2 rounded-xl bg-white hover:bg-amber-100 text-amber-900 text-sm font-bold border border-amber-300"
                  >
                    🏢 بلاغ مباشر للهيئة
                  </a>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* 📞 معلومات الشركة */}
        <Card>
          <CardBody className="text-center !py-6">
            <div className="text-3xl mb-2">🏢</div>
            <h3 className="font-black text-navy-dark text-lg mb-1">شركة صورة العقارية</h3>
            <p className="text-xs text-ink-secondary mb-4">منصة عقارية متكاملة · ملتزمة بأنظمة المملكة العربية السعودية</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-ink">
              <div><span className="text-ink-muted">📞 الهاتف:</span> <span dir="ltr" className="font-bold">0114993666</span></div>
              <div><span className="text-ink-muted">📧 الإيميل:</span> <span dir="ltr" className="font-bold">info@img.sa</span></div>
              <div><span className="text-ink-muted">🌐 الموقع:</span> <span dir="ltr" className="font-bold">img.sa</span></div>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ Sub Components ━━━━━━━━━━━━━━━━━━━━

function AuthorityCard({ icon, name, shortName, role, services, link, linkLabel }: {
  icon: string; name: string; shortName: string; role: string; services: string[]; link: string; linkLabel: string;
}) {
  return (
    <Card className="!h-full hover:shadow-brand-lg transition">
      <CardBody>
        <div className="text-4xl mb-2">{icon}</div>
        <h3 className="font-black text-navy-dark text-base">{name}</h3>
        <div className="text-[10px] text-gold-dark font-bold mb-2">{shortName}</div>
        <p className="text-xs text-ink-secondary mb-3 leading-relaxed">{role}</p>
        <div className="space-y-1 mb-3">
          {services.map((s) => (
            <div key={s} className="text-[11px] text-ink flex items-start gap-1">
              <span className="text-green-600 shrink-0">✓</span>
              <span>{s}</span>
            </div>
          ))}
        </div>
        <a href={link} target="_blank" rel="noreferrer"
           className="block text-center text-xs text-navy-dark font-bold hover:underline">
          🔗 {linkLabel}
        </a>
      </CardBody>
    </Card>
  );
}

function Step({ n, icon, title, text, externalLink, externalLinkLabel }: {
  n: number; icon: string; title: string; text: string; externalLink?: string; externalLinkLabel?: string;
}) {
  return (
    <Card className="hover:shadow-brand transition">
      <CardBody>
        <div className="flex gap-4">
          <div className="shrink-0">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-gold-dark to-gold text-white flex items-center justify-center text-lg font-black">
              {n}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-2xl">{icon}</span>
              <h3 className="font-black text-navy-dark">{title}</h3>
            </div>
            <p className="text-sm text-ink-secondary leading-relaxed mb-2">{text}</p>
            {externalLink && externalLinkLabel && (
              <a href={externalLink} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1 text-xs text-navy-dark font-bold hover:underline">
                🔗 {externalLinkLabel}
              </a>
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function Guarantee({ icon, title, text }: { icon: string; title: string; text: string }) {
  return (
    <Card className="!h-full">
      <CardBody>
        <div className="flex gap-3">
          <div className="text-3xl shrink-0">{icon}</div>
          <div className="min-w-0 flex-1">
            <h3 className="font-black text-navy-dark mb-1 text-sm">{title}</h3>
            <p className="text-xs text-ink-secondary leading-relaxed">{text}</p>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}
